/*
 * PROD-7828 R6 — pure adapters for the session page's penalties (GET /penalties/session). Rows go
 * through the Penalties tab's row_view, so the tab and this page render the same row from the same
 * payload; this file adds only what is session-specific: the context line, the history and the lookups
 * the modal needs.
 */
import { EM_DASH, month_label } from '../../teacher_payments/format';
import {
  default_pcts_map,
  free_pass_view,
  row_view,
  selection_total_text,
  who_text
} from '../../teacher_payments/penalties/adapters';
import { wire_month } from '../../teacher_payments/penalties/requests';

// "FINISHED" -> "Finished", "STUDENT_NO_SHOW" -> "Student no show" (sentence case, never shouted).
const sentence = (code) => {
  const t = String(code || '')
    .replace(/_/g, ' ')
    .toLowerCase();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
};

// The IST month a session-linked penalty is charged in: the session's own (CD-179).
export const session_month = (start_ist) => {
  const m = String(start_ist || '').match(/^(\d{4}-\d{2})-\d{2}/);
  return m ? m[1] : '';
};

export const HISTORY_ACTION_LABELS = {
  system_applied: 'Applied by the system',
  add: 'Added',
  correct: 'Corrected',
  reclassify: 'Reclassified',
  waive: 'Waived',
  unwaive: 'Waiver undone',
  revert_override: 'Reverted to the system value',
  remove: 'Removed'
};

export const session_view = (data) => {
  const d = data || {};
  const s = d.session || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map(row_view);
  const names = {};
  rows.forEach((r) => {
    names[r.teacher_uuid] = r.teacher_name;
  });
  const pay_month = session_month(s.start_ist);
  return {
    session_uuid: s.uuid || '',
    // No dangling " · " when a part is missing (CD-92).
    context_text: [
      s.uuid ? `Session ${String(s.uuid).slice(-8)}` : '',
      s.start_ist ? `${s.start_ist} IST` : '',
      s.duration_mins ? `${s.duration_mins} mins` : '',
      sentence(s.status)
    ]
      .filter(Boolean)
      .join(' · '),
    pay_month,
    month_text: month_label(pay_month),
    rows,
    charged_text: selection_total_text(rows),
    history: (Array.isArray(d.history) ? d.history : []).map((h, i) => ({
      key: `${h.penalty_uuid || ''}|${h.at_ist || ''}|${h.action || ''}|${i}`,
      at_text: h.at_ist ? `${h.at_ist} IST` : EM_DASH,
      by_text: who_text(h.actor_name, h.actor),
      teacher_name: who_text(names[h.teacher_uuid], h.teacher_uuid),
      action_text: HISTORY_ACTION_LABELS[h.action] || sentence(h.action),
      summary: h.summary || '',
      reason: h.reason || ''
    })),
    issues: Array.isArray(d.issues) ? d.issues : [],
    teacher_names: names,
    free_pass: Array.isArray(d.free_pass) ? d.free_pass : [],
    // CD-231: same live-policy map as the tab's summary, so the session Add dialog prefills identically.
    default_pcts: default_pcts_map(d.default_pcts)
  };
};

// The row's teacher-month free-pass state from the same payload (display only; the write re-validates).
export const pass_state_for = (view, row) => {
  const hit = view.free_pass.find(
    (f) =>
      f.teacher_uuid === row.teacher_uuid && wire_month(f.pay_month) === wire_month(row.pay_month)
  );
  return hit ? free_pass_view(hit) : null;
};
